"use client";

import { cn } from "@gettactic/helpers";
import { AlertTriangle, Check } from "lucide-react";
import React, { useRef, useState } from "react";
import {
  dragSelectionTo,
  type MinuteRange,
  minutesToTimeLabel,
  type ResourceAvailabilityStatus,
  type ResourceBusyBlock,
  resizeSelectionTo
} from "@/features/workplace-requests/lib/resourceAvailability";

const MINUTES_PER_HOUR = 60;
/** One axis label every two hours keeps them apart down to a phone's width. */
const AXIS_STEP_MINUTES = 2 * MINUTES_PER_HOUR;

/** One requested day as a row of the bar. */
export interface ResourceAvailabilityRow {
  key: string;
  /** e.g. "Sep 18". Shown beside the row when there is more than one. */
  label: string;
  blocks: ResourceBusyBlock[];
}

export interface ResourceAvailabilityBarProps {
  resourceName: string;
  /** Beside the resource name, e.g. "Sep 18" or "3 days". */
  heading: string;
  /** Right-hand caption, e.g. "5 booked · 7h free". */
  summary: string;
  window: MinuteRange;
  rows: ResourceAvailabilityRow[];
  /** The one window the request asks for, drawn on every row. */
  selection: MinuteRange | null;
  /**
   * The verdict for a window: free or clashing, and the line saying so.
   * Asked for the window being dragged too, so the handle turns amber the
   * moment it touches a booking rather than only once it is let go.
   * Omitted while there is nothing to judge yet, e.g. bookings loading.
   */
  statusOf?: (
    selection: MinuteRange | null
  ) => ResourceAvailabilityStatus | null;
  /**
   * A click on a track, as the minute of the day under the pointer. The
   * caller decides what window that asks for; without it the bar is read-only.
   */
  onPickMinutes?: (minutes: number) => void;
  /**
   * The window the requester dragged the handle to — by an edge to change
   * the start or the end, by the middle to move it whole. Written once, when
   * the handle is let go; without it the handle does not drag.
   */
  onChangeSelection?: (selection: MinuteRange) => void;
}

/** Which part of the handle a drag holds. */
type HandleGrip = "start" | "end" | "move";

type HandleDrag = {
  grip: HandleGrip;
  /** The track the drag started on; every row shares the same axis. */
  track: DOMRect;
  /** How far past the window's start the pointer grabbed a moving handle. */
  grabOffsetMinutes: number;
};

/** Where a minute falls along the track, as a percentage of its width. */
const percentAlong = (minutes: number, window: MinuteRange): number => {
  const span = window.endMinutes - window.startMinutes;
  const clamped = Math.min(
    window.endMinutes,
    Math.max(window.startMinutes, minutes)
  );
  return ((clamped - window.startMinutes) / span) * 100;
};

const axisMinutes = (window: MinuteRange): number[] => {
  const marks: number[] = [];
  const first =
    Math.ceil(window.startMinutes / AXIS_STEP_MINUTES) * AXIS_STEP_MINUTES;
  for (let mark = first; mark <= window.endMinutes; mark += AXIS_STEP_MINUTES) {
    marks.push(mark);
  }
  return marks;
};

const isHandleGrip = (value: string | undefined): value is HandleGrip =>
  value === "start" || value === "end" || value === "move";

const shortHourLabel = (minutes: number): string =>
  minutesToTimeLabel(minutes).replace(":00", "").replace(" ", "").toLowerCase();

/**
 * The resource's day squeezed into one row under the time pickers: gray where
 * it is booked, a colored handle where the request wants it, and one line
 * underneath saying whether the two collide. A request over several days
 * stacks one row per day; the handle is the same on all of them, because the
 * request has one window, and it only turns the "free" color when every day
 * is free at that time.
 *
 * Awareness, not a gate: a conflict is something the requester should know,
 * not something the form stops them over. What it offers is another way to
 * give the pickers' answer — a click on the track moves the window there, and
 * the handle drags: by an edge to change the start or the end, by the middle
 * to move it whole.
 */
export const ResourceAvailabilityBar: React.FC<
  ResourceAvailabilityBarProps
> = ({
  resourceName,
  heading,
  summary,
  window,
  rows,
  selection,
  statusOf,
  onPickMinutes,
  onChangeSelection
}) => {
  const hasRowLabels = rows.length > 1;
  const isDraggable = !!onChangeSelection && !!selection;

  // The handle follows the pointer locally and the answer is written once, on
  // release: writing every step would re-validate the whole form under the
  // pointer, and a drag abandoned half-way would leave a window behind.
  const dragRef = useRef<HandleDrag | null>(null);
  const [draft, setDraft] = useState<MinuteRange | null>(null);
  const shownSelection = draft ?? selection;
  const status = statusOf?.(shownSelection) ?? null;
  const isConflict = status?.tone === "conflict";

  const minutesAt = (clientX: number, track: DOMRect): number =>
    window.startMinutes +
    ((clientX - track.left) / track.width) *
      (window.endMinutes - window.startMinutes);

  const draggedSelection = (clientX: number): MinuteRange | null => {
    const drag = dragRef.current;
    if (!drag || !selection) return null;
    const pointer = minutesAt(clientX, drag.track);
    if (drag.grip === "move") {
      return dragSelectionTo(
        pointer - drag.grabOffsetMinutes,
        window,
        selection
      );
    }
    return resizeSelectionTo(drag.grip, pointer, window, selection);
  };

  const handleHandlePointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (!isDraggable || !selection) return;
    const track = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!track || track.width === 0) return;
    const grip = (event.target as HTMLElement).dataset.grip;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      grip: isHandleGrip(grip) ? grip : "move",
      track,
      grabOffsetMinutes:
        minutesAt(event.clientX, track) - selection.startMinutes
    };
    setDraft(selection);
  };

  const handleHandlePointerMove = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const next = draggedSelection(event.clientX);
    if (next) setDraft(next);
  };

  const handleHandlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const next = draggedSelection(event.clientX);
    dragRef.current = null;
    setDraft(null);
    if (
      next &&
      selection &&
      (next.startMinutes !== selection.startMinutes ||
        next.endMinutes !== selection.endMinutes)
    ) {
      onChangeSelection?.(next);
    }
  };

  const handleHandlePointerCancel = () => {
    dragRef.current = null;
    setDraft(null);
  };

  const handleTrackClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onPickMinutes) return;
    const track = event.currentTarget.getBoundingClientRect();
    if (track.width === 0) return;
    const along = (event.clientX - track.left) / track.width;
    onPickMinutes(
      window.startMinutes + along * (window.endMinutes - window.startMinutes)
    );
  };

  /*
   * Positions are data, not design: every block and the handle sit at a
   * percentage of the day that only exists at render time, which is what
   * inline styles are for here.
   */
  const renderTrack = (row: ResourceAvailabilityRow) => (
    <div
      className={cn(
        "relative overflow-hidden rounded-md border border-neutral-200 bg-neutral-50",
        hasRowLabels ? "h-6" : "h-8",
        onPickMinutes && "cursor-pointer"
      )}
      onClick={handleTrackClick}
      role="img"
      aria-label={`${resourceName}'s bookings on ${row.label}`}
    >
      {axisMinutes(window).map((mark) => (
        <div
          key={mark}
          className="absolute inset-y-0 w-px bg-neutral-200"
          style={{ left: `${percentAlong(mark, window)}%` }}
        />
      ))}
      {row.blocks.map((block) => (
        <div
          key={block.id}
          className="availability-busy absolute top-1 bottom-1 rounded-sm"
          style={{
            left: `${percentAlong(block.startMinutes, window)}%`,
            width: `${
              percentAlong(block.endMinutes, window) -
              percentAlong(block.startMinutes, window)
            }%`
          }}
          title={`${block.title ?? "Busy"} · ${minutesToTimeLabel(block.startMinutes)} – ${minutesToTimeLabel(block.endMinutes)}`}
        />
      ))}
      {shownSelection ? (
        <div
          className={cn(
            "absolute -inset-y-px flex items-center justify-between rounded-md border",
            isConflict
              ? "border-amber-700 bg-amber-500"
              : "border-indigo-700 bg-primary",
            isDraggable &&
              "cursor-grab touch-none select-none active:cursor-grabbing"
          )}
          style={{
            left: `${percentAlong(shownSelection.startMinutes, window)}%`,
            width: `${
              percentAlong(shownSelection.endMinutes, window) -
              percentAlong(shownSelection.startMinutes, window)
            }%`
          }}
          title={`${minutesToTimeLabel(shownSelection.startMinutes)} – ${minutesToTimeLabel(shownSelection.endMinutes)}`}
          onPointerDown={handleHandlePointerDown}
          onPointerMove={handleHandlePointerMove}
          onPointerUp={handleHandlePointerUp}
          onPointerCancel={handleHandlePointerCancel}
          // A press on the handle is a drag, never a click on the track
          // underneath asking to move the window to where it already is.
          onClick={(event) => event.stopPropagation()}
        >
          {isDraggable ? (
            <>
              <span
                data-grip="start"
                className="flex h-full w-2 shrink-0 cursor-ew-resize items-center justify-center"
              >
                <span className="pointer-events-none h-3 w-0.5 rounded-full bg-white" />
              </span>
              <span
                data-grip="end"
                className="flex h-full w-2 shrink-0 cursor-ew-resize items-center justify-center"
              >
                <span className="pointer-events-none h-3 w-0.5 rounded-full bg-white" />
              </span>
            </>
          ) : (
            <span className="mx-auto h-3 w-0.5 rounded-full bg-white" />
          )}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2 text-neutral-500 text-xs">
        <span className="min-w-0 truncate font-semibold uppercase tracking-wide">
          {resourceName} · {heading}
        </span>
        <span className="shrink-0">{summary}</span>
      </div>

      {/*
       * The day labels sit in a column of their own so every track starts at
       * the same x and one axis underneath serves them all. Past a handful of
       * days the list scrolls rather than pushing the rest of the form away.
       */}
      <div className="flex max-h-56 flex-col gap-1 overflow-y-auto">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center gap-2">
            {hasRowLabels ? (
              <span className="w-12 shrink-0 text-neutral-500 text-xs">
                {row.label}
              </span>
            ) : null}
            <div className="min-w-0 flex-1">{renderTrack(row)}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        {hasRowLabels ? <span className="w-12 shrink-0" /> : null}
        <div className="relative h-4 flex-1 text-[10px] text-neutral-400">
          {axisMinutes(window).map((mark) => (
            <span
              key={mark}
              className="absolute -translate-x-1/2"
              style={{ left: `${percentAlong(mark, window)}%` }}
            >
              {shortHourLabel(mark)}
            </span>
          ))}
        </div>
      </div>

      {status ? (
        <div
          className={cn(
            "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
            isConflict
              ? "border-amber-100 bg-amber-50 text-amber-700"
              : "border-green-100 bg-green-50 text-green-700"
          )}
          role="status"
        >
          {isConflict ? (
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          ) : (
            <Check className="mt-px h-3.5 w-3.5 shrink-0" />
          )}
          <span>{status.text}</span>
        </div>
      ) : null}
    </div>
  );
};
